import type { TemplatePortfolioDraft } from "@adpilot/shared";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/stores/auth";
import { useTemplatePortfolioDraft } from "./api";
import { StepBasics } from "./StepBasics";
import { StepEnable } from "./StepEnable";
import { StepExecute } from "./StepExecute";
import { StepKeywords } from "./StepKeywords";
import { StepPreview } from "./StepPreview";
import { TemplatePortfolioShell, type TemplatePortfolioStep } from "./TemplatePortfolioShell";

export interface TemplatePortfolioSearch {
  step?: TemplatePortfolioStep;
  shopId?: string;
  draftId?: string;
  executionId?: string;
}

/** Without an explicit step, resume at the furthest step the URL supports. */
export function resolveTemplatePortfolioStep(
  search: TemplatePortfolioSearch,
): TemplatePortfolioStep {
  const furthest: TemplatePortfolioStep = search.executionId ? 4 : search.draftId ? 2 : 1;
  if (!search.step) return furthest;
  if (search.step >= 4 && !search.executionId) return furthest;
  if (search.step >= 2 && !search.draftId) return 1;
  return search.step;
}

/**
 * /campaigns/template-portfolio — the URL holds the whole position (step, shop, draft,
 * execution) so a refresh resumes. The shop is the draft's shop, not the ambient one.
 */
export function TemplatePortfolioWizard() {
  const { t } = useTranslation("templatePortfolio");
  const search = useSearch({ from: "/_authed/campaigns/template-portfolio" });
  const navigate = useNavigate({ from: "/campaigns/template-portfolio" });
  const currentShopId = useAuthStore((state) => state.currentShopId);
  const shopId = search.shopId ?? currentShopId;
  const step = resolveTemplatePortfolioStep(search);
  const draftQuery = useTemplatePortfolioDraft(shopId ?? "", search.draftId);

  const go = (patch: TemplatePortfolioSearch) =>
    void navigate({ search: (previous) => ({ ...previous, ...patch }) });

  let content: React.ReactNode;
  if (search.draftId && draftQuery.isPending) {
    content = <Skeleton className="h-96 w-full" />;
  } else if (search.draftId && draftQuery.isError) {
    content = (
      <ErrorState
        message={t("loadError")}
        onRetry={() => void draftQuery.refetch()}
        isRetrying={draftQuery.isFetching}
      />
    );
  } else if (step === 1 || !draftQuery.data || !shopId) {
    content = (
      <StepBasics
        key={`${shopId}:${search.draftId ?? "new"}`}
        shopId={shopId}
        draft={draftQuery.data}
        onShopChange={(nextShopId) =>
          go({ step: 1, shopId: nextShopId, draftId: undefined, executionId: undefined })
        }
        onSaved={(draft: TemplatePortfolioDraft) =>
          go({ step: 2, shopId: draft.shopId, draftId: draft.id })
        }
      />
    );
  } else {
    const draft = draftQuery.data;
    const stepProps = { shopId, draft };
    if (step === 2) {
      content = (
        <StepKeywords
          {...stepProps}
          onBack={() => go({ step: 1 })}
          onNext={() => go({ step: 3 })}
        />
      );
    } else if (step === 3) {
      content = (
        <StepPreview
          {...stepProps}
          onBack={() => go({ step: 2 })}
          onExecutionStarted={(executionId) => go({ step: 4, executionId })}
        />
      );
    } else if (step === 4 && search.executionId) {
      content = (
        <StepExecute
          {...stepProps}
          executionId={search.executionId}
          onNext={() => go({ step: 5 })}
        />
      );
    } else if (search.executionId) {
      content = <StepEnable {...stepProps} executionId={search.executionId} />;
    }
  }

  return <TemplatePortfolioShell step={step}>{content}</TemplatePortfolioShell>;
}
